import ScrollModal from "@/components/common/scroll-modal";
import SelectField from "@/components/common/select";
import Card from "@/components/ui/card";
import { cn } from "@/utils/theme";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Heading,
  Icon,
  Input,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import { months, SelectTypeEnum, weekDaysLabels } from "../utils/utils";

export type SelectType =
  | "DAILY_INTERVAL"
  | "WEEKLY_INTERVAL"
  | "MONTHLY_INTERVAL"
  | "YEARLY_INTERVAL"
  | "MONTHLY_BY_BUSINESS_DAY";

type DateType = "date" | "day";

export interface ConfigCustomEvent {
  repeatType: SelectType;
  quantity: number | undefined;
  dayToStart: string;
  dateType: DateType;
  monthSelected: number;
  daysSelected: number[];
  repeatStart: string;
  repeatStartDay: string;
}

const repeatStartSelect = [
  {
    value: "1",
    label: "Primer",
  },
  {
    value: "2",
    label: "Segundo",
  },
  {
    value: "3",
    label: "Tercer",
  },
  {
    value: "4",
    label: "Cuarto",
  },
  {
    value: "5",
    label: "Quinto",
  },
  {
    value: "-2",
    label: "Penúltimo",
  },
  {
    value: "-1",
    label: "Ultimo",
  },
];

const getCalendarRows = (month: number, year = new Date().getFullYear()) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const rows: (number | null)[][] = [];

  for (let i = 0; i < days.length; i += 7) {
    rows.push(days.slice(i, i + 7));
  }

  return rows;
};

const CustomEvent = ({
  isOpen,
  onClose,
  configCustomEvent,
  handleConfigCustomEvent,
}: {
  isOpen: boolean;
  onClose: () => void;
  configCustomEvent?: ConfigCustomEvent;
  handleConfigCustomEvent: (config: ConfigCustomEvent) => void;
}) => {
  const [repeatType, setRepeatType] = useState<SelectType>(
    configCustomEvent?.repeatType ?? SelectTypeEnum.DAILY_INTERVAL,
  );
  const [quantity, setQuantity] = useState<number | undefined>(
    configCustomEvent?.quantity ?? undefined,
  );
  const [dayToStart, setDayToStart] = useState<string>(
    configCustomEvent?.dayToStart ?? "1",
  );
  const [dateType, setDateType] = useState<DateType>(
    configCustomEvent?.dateType ?? "date",
  );
  const [monthSelected, setMonthSelected] = useState<number>(
    configCustomEvent?.monthSelected ?? 1,
  );
  const [daysSelected, setDaysSelected] = useState<number[]>(
    configCustomEvent?.daysSelected ?? [],
  );
  const [repeatStart, setRepeatStart] = useState<string>(
    configCustomEvent?.repeatStart ?? "1",
  );
  const [repeatStartDay, setRepeatStartDay] = useState<string>(
    configCustomEvent?.repeatStartDay ?? "Dia",
  );

  const isDisabled = useMemo(() => {
    if (quantity && repeatType === SelectTypeEnum.DAILY_INTERVAL) return false;
    if (quantity && repeatType === SelectTypeEnum.WEEKLY_INTERVAL) return false;
    if (
      quantity &&
      (repeatType === SelectTypeEnum.MONTHLY_INTERVAL ||
        repeatType === SelectTypeEnum.YEARLY_INTERVAL)
    ) {
      if (dateType === "date" && daysSelected.length > 0) return false;
      if (dateType === "day") return false;
    }

    return true;
  }, [quantity, repeatType, dateType, daysSelected]);

  const handleSelectedDay = (day: number) => {
    const isSelected = daysSelected.includes(day);

    if (isSelected)
      return setDaysSelected(() => daysSelected.filter((d) => d !== day));

    setDaysSelected((prev) => [...prev, day]);
  };

  const calendarRows = useMemo(
    () => getCalendarRows(monthSelected - 1),
    [monthSelected],
  );

  const handleCustomEvents = () => {
    const config: ConfigCustomEvent = {
      repeatType,
      quantity,
      dayToStart,
      dateType,
      monthSelected,
      daysSelected,
      repeatStart,
      repeatStartDay,
    };
    handleConfigCustomEvent(config);
    onClose();
  };
  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] h-[450px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="mb-0!">
            Configurar repetición
          </Heading>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-xl text-gray-500" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full flex py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={onClose}
            className="cursor-pointer flex-1"
          >
            Cancelar
          </Button>
          <Button
            variant="secondary"
            size="sm"
            type="submit"
            className="flex-1 cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={isDisabled}
            onClick={handleCustomEvents}
          >
            Aceptar
          </Button>
        </div>
      }
    >
      <form
        id="new-process-form"
        onSubmit={(e) => {
          e.preventDefault();
          //   handleSubmit(daysSelected);
          return onClose();
        }}
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        <div className="flex flex-col gap-2 pb-6">
          <div>
            <label className="grid-cols-2 font-semibold text-body-loud leading-l text-sm">
              Repetir cada<span className="text-danger">*</span>
            </label>

            <GroupTextField label="">
              <GroupTextFieldRow className="w-full items-center">
                <Input
                  group="left"
                  className="w-full"
                  placeholder="Cantidad"
                  value={quantity}
                  min={0}
                  onChange={(e) =>
                    e.target.value === "0"
                      ? setQuantity(undefined)
                      : setQuantity(Number(e.target.value))
                  }
                  type="number"
                />
                <Select
                  name="repeatType"
                  value={repeatType}
                  onValueChange={(value) => setRepeatType(value as SelectType)}
                >
                  <SelectTrigger group="right" className="font-bold">
                    <SelectValue
                      placeholder="Select an option"
                      className="font-bold"
                    />
                  </SelectTrigger>
                  <SelectContent className="border-neutral">
                    {[
                      { label: "Dias", key: SelectTypeEnum.DAILY_INTERVAL },
                      { label: "Semanas", key: SelectTypeEnum.WEEKLY_INTERVAL },
                      { label: "Meses", key: SelectTypeEnum.MONTHLY_INTERVAL },
                      { label: "Años", key: SelectTypeEnum.YEARLY_INTERVAL },
                    ].map((type) => (
                      <SelectItem key={type.key} value={type.key}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </GroupTextFieldRow>
            </GroupTextField>
          </div>
          {repeatType === SelectTypeEnum.WEEKLY_INTERVAL && (
            <div>
              <label className="grid-cols-2 w-full font-semibold text-body-loud leading-l text-sm">
                En el día<span className="text-danger">*</span>
              </label>
              <SelectField
                label=""
                placeholder=""
                name="dayToStart"
                value={dayToStart}
                options={[
                  {
                    value: "1",
                    label: "Lunes",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                  {
                    value: "2",
                    label: "Martes",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                  {
                    value: "3",
                    label: "Miércoles",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                  {
                    value: "4",
                    label: "Jueves",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                  {
                    value: "5",
                    label: "Viernes",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                  {
                    value: "6",
                    label: "Sábado",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                  {
                    value: "7",
                    label: "Domingo",
                    avatar: <Icon type="far" name="house" />,
                    iconPosition: "left",
                  },
                ]}
                onChange={(value) => {
                  setDayToStart(value);
                }}
                isLoading={false}
              />
            </div>
          )}

          {(repeatType === SelectTypeEnum.YEARLY_INTERVAL ||
            repeatType === SelectTypeEnum.MONTHLY_INTERVAL) && (
            <>
              <SegmentedControl
                defaultValue="date"
                value={dateType}
                onValueChange={(value) => setDateType(value as DateType)}
                className="w-full font-semibold text-sm"
              >
                <SegmentedControlItem value="date">
                  En una fecha
                </SegmentedControlItem>
                <SegmentedControlItem value="day">
                  En un día
                </SegmentedControlItem>
              </SegmentedControl>

              {repeatType === SelectTypeEnum.YEARLY_INTERVAL && (
                <div>
                  <label className="w-full font-semibold text-body-loud leading-l text-sm">
                    En el mes<span className="text-danger">*</span>
                  </label>
                  <Select
                    name="month"
                    value={monthSelected.toString()}
                    onValueChange={(value) => setMonthSelected(Number(value))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="border-neutral">
                      {months.map((month, index) => (
                        <SelectItem key={month} value={(index + 1).toString()}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {dateType === "date" && (
                <Card>
                  <div className="grid grid-rows-5 items-center">
                    {calendarRows.map((row, rowIndex) => (
                      <div
                        key={rowIndex}
                        className={cn("grid grid-cols-7 mb-1")}
                      >
                        {row.map(
                          (day, i) =>
                            day && (
                              <Button
                                key={i}
                                size="xs"
                                type="button"
                                variant={
                                  daysSelected.includes(day)
                                    ? "default"
                                    : "ghost"
                                }
                                className="w-8 h-8 rounded-full font-light flex items-center justify-center"
                                onClick={() => handleSelectedDay(day)}
                              >
                                {day}
                              </Button>
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {dateType === "day" && (
                <div>
                  <label className="grid-cols-2 font-semibold text-body-loud leading-l text-sm">
                    En su<span className="text-danger">*</span>
                  </label>

                  <GroupTextField label="">
                    <GroupTextFieldRow className="w-full items-center">
                      <Select
                        name="repeatStart"
                        value={repeatStart}
                        onValueChange={(value) => setRepeatStart(value)}
                      >
                        <SelectTrigger group="left" className="w-full">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent className="border-neutral">
                          {repeatStartSelect.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        name="repeatStartDay"
                        value={repeatStartDay}
                        onValueChange={(value) =>
                          setRepeatStartDay(value as SelectType)
                        }
                      >
                        <SelectTrigger group="right" className="w-full">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent className="border-neutral">
                          {[
                            "Dia",
                            "Dia de semana",
                            "Fin de semana",
                            ...weekDaysLabels,
                          ].map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </GroupTextFieldRow>
                  </GroupTextField>
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </ScrollModal>
  );
};

export default CustomEvent;
